<svelte:head>
    <title>{ import.meta.env.VITE_WEBSITE_TITLE }</title> <!-- eslint-disable-line -->
</svelte:head>

<script>
    import { firebase, auth } from '$lib/firebase/firebase';
    import { Loader, Toasts } from '@repo/ui';
    import { Header } from '@repo/ui/header';
    import { hasAction, hasRole } from '$lib/access';
    import { getCurrentUser, signOut, getAppSwitchTargets, switchToApp } from '$lib/Auth';
    import { currentUser } from '$lib/stores';
    import { setUserData } from '$lib/DB';
    import { actions, sections, roles } from '$lib/Global';
    import { headerImage } from '@repo/config/Settings';
    import { TARGET_APPS } from '@repo/config/Global.js';
    import { watchAppSwitchTargets } from '@repo/firebase';
    import { TranslationSettingsModal } from '@repo/translation';
    import BookAccordionProvider from '$lib/book-accordion/BookAccordionProvider.svelte';
    import { ghostToken } from '@repo/stores/Ghost.js';
    import { stopImpersonating } from '$lib/Impersonation';
    import { onDestroy } from 'svelte';
    import './styles.css';

    const tapMenuItems = [
        { icon: 'translate', label: 'translation settings', action: 'translation-settings' },
        { label: 'Sign Out', icon: 'logout', action: 'sign-out' },
    ];
    /**
     * @typedef {Object} Props
     * @property {import('svelte').Snippet} [children]
     */
    let { children } = $props();


    firebase;

    let availableApps = $state(null);
    let isImpersonating = $state(false);
    let impersonatedUser = $state(null);
    let impersonationExpiryTime = $state(null);

    const unsubscribeImpersonation = ghostToken.subscribe((token) => {
        isImpersonating = token !== null;
    });

    const unsubscribeUser = getCurrentUser().subscribe(async (u) => {
        impersonatedUser = u;
        if (!u || !isImpersonating) return;
        try {
            const token = await auth.currentUser?.getIdTokenResult();
            const expiresAt = token?.claims?.impersonationExpiresAt;
            impersonationExpiryTime = expiresAt
                ? new Date(expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                : null;
        } catch (err) {
            console.warn('[tap-impersonation] failed to read expiry claim', err);
        }
    });

    const unsubscribeApps = watchAppSwitchTargets(getCurrentUser(), {
        load: getAppSwitchTargets,
        onTargets: (targets) => {
            availableApps = targets;
        },
        onError: (err) => console.warn('[tap-header] failed to load user apps', err),
    });

    onDestroy(() => {
        try { unsubscribeImpersonation(); } catch { /* ignore */ }
        try { unsubscribeUser(); } catch { /* ignore */ }
        try { unsubscribeApps(); } catch { /* ignore */ }
    });

    function getDisplayName(u) {
        return u?.displayName
            || u?.systemData?.displayName
            || u?.email
            || 'user';
    }

    async function handleSwitchApp(targetApp) {
        try {
            await switchToApp(targetApp);
        } catch (err) {
            console.error('[tap-header] failed to switch app', err);
        }
    }
</script>

<BookAccordionProvider>
    <div class="biozone-world semi-interactive {isImpersonating ? 'is-impersonating' : ''}">
        <Header
            {getCurrentUser}
            {signOut}
            {hasAction}
            {hasRole}
            {actions}
            {sections}
            {roles}
            {headerImage}
            menuItems={tapMenuItems}
            showManageLink={false}
            showNotifications={false}
            {availableApps}
            currentApp={TARGET_APPS.TAP}
            onSwitchApp={handleSwitchApp}
        >
            <TranslationSettingsModal user={$currentUser} onSave={setUserData} />
        </Header>
        <main class="stage">
            <section id="section_stage" class="section-stage">
                {@render children?.()}

                {#if isImpersonating}
                    <div class="impersonation-notice">
                        <!-- svelte-ignore a11y_click_events_have_key_events -->
                        <!-- svelte-ignore a11y_no_static_element_interactions -->
                        <p>
                            Impersonating user: <b>{getDisplayName(impersonatedUser)}</b>
                            {#if impersonationExpiryTime} | Expires at {impersonationExpiryTime}{/if}
                            | <span onclick={stopImpersonating}>Stop Impersonating</span>
                        </p>
                    </div>
                {/if}
            </section>
        </main>
        <Toasts />
        <Loader />
    </div>
</BookAccordionProvider>

<style>
    /* Education's stage: fills the space under the header. min-height: 0
       lets it shrink to the viewport instead of growing with its content. */
    .stage {
        display: block;
        background-color: #262424;
        flex-grow: 1;
        min-height: 0;
    }

    .section-stage {
        position: relative;
        display: flex;
        height: 100%;
    }
</style>
